import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, friendlyError } from "../lib/supabase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { itemFromRow, itemToInsert, itemToUpdate, patchToUpdate } from "./mappers.js";
import { today } from "../domain/format.js";
import { derivedTotals } from "../domain/history.js";
import { uploadPhoto, deletePhoto, signPaths, validateImage } from "../lib/storage.js";

/* The single source of truth for a collector's items.
   Mutations apply optimistically so taps stay instant, then persist; if the
   write fails the optimistic change is rolled back and the error surfaced,
   because silently keeping a change that never reached the database is the
   worst possible outcome for a collection record. */
export function useItems() {
  const { userId } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // path -> short-lived signed URL. The bucket is private, so a stored path is
  // not renderable on its own.
  const [photoUrls, setPhotoUrls] = useState({});

  /* Mutations need to read the current list — to snapshot it for a rollback, or
     to find the item they're about to change. Closing over `items` would give
     them whatever the list looked like when the callback was created, so two
     taps in quick succession could roll back to a stale snapshot. Reading
     through a ref instead keeps them correct and keeps their identity stable,
     which stops every screen re-rendering each time any item changes. */
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const load = useCallback(async () => {
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const [itemsRes, historyRes] = await Promise.all([
      supabase.from("items").select("*").eq("user_id", userId).order("created_at", { ascending: false }),
      supabase.from("item_history").select("*").eq("user_id", userId).order("date", { ascending: true }),
    ]);

    if (itemsRes.error || historyRes.error) {
      setError(friendlyError(itemsRes.error || historyRes.error, "Couldn't load your collection."));
      setLoading(false);
      return;
    }

    const byItem = new Map();
    for (const h of historyRes.data || []) {
      if (!byItem.has(h.item_id)) byItem.set(h.item_id, []);
      byItem.get(h.item_id).push(h);
    }

    const loaded = (itemsRes.data || []).map((row) => itemFromRow(row, byItem.get(row.id) || []));
    setItems(loaded);
    setLoading(false);

    // Sign every photo path in one round trip rather than per image.
    setPhotoUrls(await signPaths(loaded.flatMap((i) => i.photos)));
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Applies `patch` locally, runs `persist`, and rolls back if it fails. */
  const optimistic = useCallback(async (id, patch, persist) => {
    const snapshot = itemsRef.current;
    setSaveError(null);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch(i) } : i)));
    const { error: err } = await persist();
    if (err) {
      setItems(snapshot);
      setSaveError(friendlyError(err, "Couldn't save that change."));
      return false;
    }
    return true;
  }, []);

  const addItem = useCallback(
    async (data) => {
      if (!userId) return { error: "Not signed in." };
      setSaveError(null);
      const { data: row, error: err } = await supabase
        .from("items")
        .insert(itemToInsert(data, userId))
        .select()
        .single();

      if (err) {
        const message = friendlyError(err, "Couldn't save that item.");
        setSaveError(message);
        return { error: message };
      }
      setItems((prev) => [itemFromRow(row, []), ...prev]);
      return { error: null, item: itemFromRow(row, []) };
    },
    [userId]
  );

  /** Save edits to an item's details. Unlike the tap-sized mutations below this
   *  is not optimistic: the user is watching a form and expects a definite
   *  answer, and a silent rollback under a "Saved" is worse than a slow save. */
  const updateItem = useCallback(async (id, data) => {
    setSaveError(null);
    const { data: row, error: err } = await supabase
      .from("items")
      .update(itemToUpdate(data))
      .eq("id", id)
      .select()
      .single();

    if (err) {
      const message = friendlyError(err, "Couldn't save your changes.");
      setSaveError(message);
      return { error: message };
    }
    // History lives in separate rows, so carry it across rather than re-fetching.
    setItems((prev) => prev.map((i) => (i.id === id ? { ...itemFromRow(row, []), history: i.history } : i)));
    return { error: null };
  }, []);

  /** Remove an item. Its history rows cascade away in the database. */
  const deleteItem = useCallback(async (id) => {
    const snapshot = itemsRef.current;
    const doomed = snapshot.find((i) => i.id === id);
    setSaveError(null);
    setItems((prev) => prev.filter((i) => i.id !== id));

    const { error: err } = await supabase.from("items").delete().eq("id", id);
    if (err) {
      setItems(snapshot);
      const message = friendlyError(err, "Couldn't delete that item.");
      setSaveError(message);
      return { error: message };
    }
    // Nothing cascades into Storage, so the item's photos would otherwise sit in
    // the bucket forever. The row is already gone either way, so this is best
    // effort — a leftover object is invisible and harmless, just wasted space.
    for (const path of doomed?.photos || []) deletePhoto(path).catch(() => {});
    return { error: null };
  }, []);

  /** Log any of the three history actions. The database does the increment.
   *  The entry id is minted here rather than by the database: the Flight
   *  History list can delete and re-date entries, and it can only do that to a
   *  row it can name. Generating it client-side means a just-logged Flight is
   *  editable immediately, instead of only after the next full reload. */
  const logAction = useCallback(
    async (id, action) => {
      const date = today();
      const entryId = crypto.randomUUID();
      return optimistic(
        id,
        (i) => {
          const patch = { history: [...i.history, { id: entryId, action, date }] };
          if (action === "Worn") {
            patch.wears = i.wears + 1;
            patch.lastWorn = date;
          }
          if (action === "Cleaned") {
            patch.lastCleaned = date;
            patch.condition = Math.min(100, i.condition + 3);
          }
          if (action === "Rotated") patch.lastRotated = date;
          return patch;
        },
        () => supabase.rpc("log_item_action", { p_item_id: id, p_action: action, p_date: date, p_entry_id: entryId })
      );
    },
    [optimistic]
  );

  /** Log a Flight / Outing / Carry — the category decides what it's called. */
  const logWear = useCallback((id) => logAction(id, "Worn"), [logAction]);

  /* ---------------- editing logged history ----------------
     Both go through SQL functions that recompute wears and the last-worn /
     cleaned / rotated dates from the surviving rows, so the totals can't drift
     from the entries they're derived from. */

  /** Applies a new history array locally, persists via `rpc`, rolls back on failure. */
  const withHistory = useCallback(
    async (itemId, nextHistory, rpc, failureMessage) => {
      const snapshot = itemsRef.current;
      setSaveError(null);
      setItems((prev) =>
        prev.map((i) => (i.id === itemId ? { ...i, history: nextHistory, ...derivedTotals(nextHistory) } : i))
      );

      const { data: row, error: err } = await rpc();
      if (err) {
        setItems(snapshot);
        const message = friendlyError(err, failureMessage);
        setSaveError(message);
        return { error: message };
      }
      // Reconcile with what the database actually computed.
      if (row) {
        setItems((prev) =>
          prev.map((i) => (i.id === itemId ? { ...itemFromRow(row, []), history: nextHistory } : i))
        );
      }
      return { error: null };
    },
    []
  );

  const deleteHistoryEntry = useCallback(
    (itemId, entryId) => {
      const item = itemsRef.current.find((i) => i.id === itemId);
      if (!item) return Promise.resolve({ error: "Item not found." });
      return withHistory(
        itemId,
        item.history.filter((h) => h.id !== entryId),
        () => supabase.rpc("delete_history_entry", { p_entry_id: entryId }),
        "Couldn't remove that entry."
      );
    },
    [withHistory]
  );

  /** Re-date one entry. The Flight History row commits on the way out of the
   *  field, so this runs once per edit rather than once per keystroke. The year
   *  guard is belt and braces: a date input reports 0002 / 0020 / 0202 while
   *  2027 is being typed, and none of those belong in a collection record. */
  const updateHistoryEntry = useCallback(
    (itemId, entryId, date) => {
      const item = itemsRef.current.find((i) => i.id === itemId);
      if (!item || !date || Number(date.slice(0, 4)) < 1900) return Promise.resolve({ error: null });
      return withHistory(
        itemId,
        item.history.map((h) => (h.id === entryId ? { ...h, date } : h)),
        () => supabase.rpc("update_history_entry", { p_entry_id: entryId, p_date: date }),
        "Couldn't change that date."
      );
    },
    [withHistory]
  );

  /* ---------------- photos ---------------- */

  /** Uploads files to Storage and appends their paths to the item. */
  const addPhotos = useCallback(
    async (itemId, files) => {
      if (!userId || !files.length) return { error: null };
      setSaveError(null);

      for (const file of files) {
        const problem = validateImage(file);
        if (problem) {
          setSaveError(problem);
          return { error: problem };
        }
      }

      // Tracked outside the try so a failed row update can clean up after the
      // uploads that did land — otherwise the files stay in the bucket with
      // nothing referencing them, and the user is told the upload failed.
      const paths = [];
      try {
        for (const file of files) paths.push(await uploadPhoto(userId, `items/${itemId}`, file));

        const item = itemsRef.current.find((i) => i.id === itemId);
        const next = [...(item?.photos || []), ...paths];

        const { error: err } = await supabase.from("items").update({ photos: next }).eq("id", itemId);
        if (err) throw err;

        setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, photos: next } : i)));
        const signed = await signPaths(paths);
        setPhotoUrls((prev) => ({ ...prev, ...signed }));
        return { error: null };
      } catch (err) {
        for (const path of paths) deletePhoto(path).catch(() => {});
        const message = friendlyError(err, "Couldn't upload that photo.");
        setSaveError(message);
        return { error: message };
      }
    },
    [userId]
  );

  /** Mints a fresh viewing link for one photo. Called when an image fails to
   *  load, which in practice means its signed URL outlived the session. */
  const refreshPhoto = useCallback(async (path) => {
    const signed = await signPaths([path]);
    if (signed[path]) setPhotoUrls((prev) => ({ ...prev, [path]: signed[path] }));
  }, []);

  /** Removes one photo from the row and from Storage. Addressed by path, not
   *  position: an upload finishing while the gallery is open shifts the
   *  positions, and deleting by index would then remove the wrong picture. */
  const removePhoto = useCallback(
    async (itemId, path) => {
      const snapshot = itemsRef.current;
      const item = snapshot.find((i) => i.id === itemId);
      if (!item || !item.photos.includes(path)) return { error: null };
      const next = item.photos.filter((p) => p !== path);

      setSaveError(null);
      setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, photos: next } : i)));

      const { error: err } = await supabase.from("items").update({ photos: next }).eq("id", itemId);
      if (err) {
        setItems(snapshot);
        const message = friendlyError(err, "Couldn't remove that photo.");
        setSaveError(message);
        return { error: message };
      }
      // The row is the source of truth; a leftover object is harmless, so a
      // failure to delete the file itself isn't worth surfacing.
      deletePhoto(path).catch(() => {});
      return { error: null };
    },
    []
  );

  const updateStatus = useCallback(
    (id, status) =>
      optimistic(
        id,
        () => ({ status }),
        () => supabase.from("items").update(patchToUpdate({ status })).eq("id", id)
      ),
    [optimistic]
  );

  /* The condition slider fires on every pixel of a drag. Update local state on
     each tick for a responsive control, but only write once the drag settles. */
  const conditionTimer = useRef(null);
  const updateCondition = useCallback(
    (id, condition) => {
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, condition } : i)));
      if (conditionTimer.current) clearTimeout(conditionTimer.current);
      conditionTimer.current = setTimeout(async () => {
        setSaveError(null);
        const { error: err } = await supabase.from("items").update(patchToUpdate({ condition })).eq("id", id);
        if (err) {
          setSaveError(friendlyError(err, "Couldn't save the condition score."));
          load();
        }
      }, 600);
    },
    [load]
  );

  useEffect(() => () => clearTimeout(conditionTimer.current), []);

  return {
    items,
    photoUrls,
    loading,
    error,
    saveError,
    dismissSaveError: () => setSaveError(null),
    reload: load,
    addItem,
    updateItem,
    deleteItem,
    deleteHistoryEntry,
    updateHistoryEntry,
    addPhotos,
    removePhoto,
    refreshPhoto,
    logWear,
    logAction,
    updateStatus,
    updateCondition,
  };
}
